// ✅ src/pages/OrderManagement/ReconciliationPrint.jsx
// Print-only page (NO sidebar / NO theme / NO scroll)
// Updated with localStorage common headers

import React, { useEffect, useMemo, useState } from "react";
import { FaPrint } from "react-icons/fa";
import axios from "axios";

const API = "http://nissi-india.com/api/reconciliation";

const toInt = (v, def = 0) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : def;
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const val = obj?.[k];
    if (val !== undefined && val !== null && String(val).trim() !== "") {
      return val;
    }
  }
  return fallback;
};

export default function ReconciliationPrint() {
  const qs = useMemo(() => new URLSearchParams(window.location.search), []);

  const contractId = useMemo(
    () =>
      toInt(
        qs.get("contractId") ||
          qs.get("contractid") ||
          sessionStorage.getItem("Selcontractid") ||
          sessionStorage.getItem("selcontractid") ||
          sessionStorage.getItem("contractId") ||
          0
      ),
    [qs]
  );

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const loginUserId = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId || "",
      "x-finyear": finYear || "",
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const [rows, setRows] = useState([]);
  const [header, setHeader] = useState({
    customerName: "",
    customerAddress: "",
    projectName: "",
  });
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    if (!contractId) {
      setRows([]);
      setHeader({
        customerName: "",
        customerAddress: "",
        projectName: "",
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/view`, {
        params: { contractId },
        headers: commonHeaders,
      });

      if (res.data?.success) {
        const list = res.data?.rows || [];
        setRows(list);

        if (list.length > 0) {
          const first = list[0];
          setHeader({
            customerName: pick(
              first,
              ["fld_Customername", "fld_customername", "CustomerName", "customerName"],
              ""
            ),
            customerAddress: pick(
              first,
              ["fld_Customeraddress", "fld_customeraddress", "CustomerAddress", "customerAddress"],
              ""
            ),
            projectName: pick(
              first,
              ["fld_Projectname", "fld_projectname", "ProjectName", "projectName"],
              ""
            ),
          });
        } else {
          setHeader({
            customerName: "",
            customerAddress: "",
            projectName: "",
          });
        }
      } else {
        setRows([]);
        setHeader({
          customerName: "",
          customerAddress: "",
          projectName: "",
        });
      }
    } catch (e) {
      console.error(e);
      setRows([]);
      setHeader({
        customerName: "",
        customerAddress: "",
        projectName: "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, commonHeaders]);

  return (
    <div className="p-6 text-black bg-white print:p-0">
      <style>{`
        @media print {
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          tr { page-break-inside: avoid; }
          .print-hide { display: none !important; }
        }
      `}</style>

      {/* ================= HEADER (Company) ================= */}
      <div className="border-2 border-black mb-3">
        <div className="text-center py-3 border-b border-black">
          <h1 className="text-lg font-bold text-red-600">Nissi Infotech</h1>
          <div className="text-sm">Nissi</div>
          <div className="text-sm font-bold">CIVIL CONSTRUCTION</div>
          <div className="text-xs">
            3/42-12, Nagappa colony, kavundampalayam – 641030, Tamil Nadu, India
          </div>
          <div className="text-xs">
            Phone: 9003744644 | Email: infraconsoft-implementation@nissiinfotech.in
          </div>
        </div>

        {/* Title */}
        <div className="text-center py-2 border-b border-black font-bold">
          Reconciliation Report
        </div>

        {/* Header block */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <div>
              <b>Customer Name</b> &nbsp; :&nbsp; {header.customerName || "-"}
            </div>
            <div>
              <b>Project Name</b> &nbsp; :&nbsp; {header.projectName || "-"}
            </div>
            <div>
              <b>Customer Address</b> :&nbsp; {header.customerAddress || "-"}
            </div>
            <div>
              <b>Client / Contract ID</b> :&nbsp; {contractId || "-"}
            </div>
          </div>
        </div>
      </div>

      {/* ================= ACTION BAR ================= */}
      <div className="flex justify-end mb-4 print-hide">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2"
        >
          <FaPrint />
          Print
        </button>
      </div>

      {/* ================= TABLE ================= */}
      <div className="border border-black">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-black">
              <th className="border-r border-black p-2 w-[60px]">S.No</th>
              <th className="border-r border-black p-2 w-[180px]">Category</th>
              <th className="border-r border-black p-2">Nature Of Work</th>
              <th className="border-r border-black p-2 w-[90px]">Unit</th>
              <th className="border-r border-black p-2 w-[110px] text-red-600">
                Billed Qty
              </th>
              <th className="border-r border-black p-2 w-[150px] text-blue-600">
                Actual Consumed Qty
              </th>
              <th className="p-2 w-[120px]">Difference (%)</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="p-4 text-center">
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-4 text-center">
                  No Records Found
                </td>
              </tr>
            ) : (
              rows.map((r, i) => {
                const category = pick(r, [
                  "fld_projectcategoryname",
                  "fld_Projectcategoryname",
                  "fld_projectcategoryName",
                ]);
                const work = pick(r, ["fld_work", "fld_Work", "work"]);
                const unit = pick(r, ["fld_unit", "fld_Unit", "unit"]);
                const billedQty = pick(r, ["fld_Qty", "fld_qty", "Qty", "qty"], "0");
                const opQty = pick(r, ["fld_opQty", "fld_opqty", "opQty", "opqty"], "0");
                const diff = pick(r, ["fld_difference", "fld_Difference", "difference"], "");

                return (
                  <tr key={i} className="border-b border-black">
                    <td className="border-r border-black p-2 text-center">{i + 1}</td>
                    <td className="border-r border-black p-2">{category}</td>
                    <td className="border-r border-black p-2 leading-relaxed">{work}</td>
                    <td className="border-r border-black p-2 text-center">{unit}</td>
                    <td className="border-r border-black p-2 text-center text-red-600">
                      {billedQty}
                    </td>
                    <td className="border-r border-black p-2 text-center text-blue-600">
                      {opQty}
                    </td>
                    <td className="p-2 text-center">{diff}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ================= FOOTER ================= */}
      <div className="mt-6 text-xs text-right">
        Printed on : {new Date().toLocaleString()}
      </div>
    </div>
  );
}